import {useState} from 'react';
import {apiPost, usePolling} from '../api';
import {Badge, Stage, fmtTime, jsonShort} from '../components';

const STATUSES = ['queued', 'dispatched', 'running', 'succeeded', 'failed',
  'timeout', 'cancelled', 'lost'];

function LogDrawer({job, onClose}) {
  // 运行中的 job 日志 6s 自动刷新
  const {data} = usePolling(`/jobs/${job.id}/log`, {tail: 200}, 6000);
  const lines = data ? data.lines : null;
  return (
    <>
      <div className="drawer-mask" onClick={onClose} />
      <div className="drawer" style={{width: 760}}>
        <button className="close" onClick={onClose}>×</button>
        <h2 style={{fontSize: 16}}>{job.name} <Badge v={job.status} /></h2>
        <dl className="kv">
          <dt>backend</dt><dd>{job.backend} <span className="muted mono">{job.backend_job_id}</span></dd>
          <dt>stage</dt><dd><Stage s={job.stage} /></dd>
          <dt>exit_code</dt><dd>{job.exit_code ?? '—'} {job.error_class && <Badge v={job.error_class} />}</dd>
          <dt>workdir</dt><dd className="mono">{job.workdir || '—'}</dd>
          <dt>submitted</dt><dd>{fmtTime(job.submitted_at)} → {fmtTime(job.finished_at)}</dd>
        </dl>
        <h3 style={{margin: '14px 0 8px'}}>command</h3>
        <pre className="logbox" style={{maxHeight: 100}}>{job.command || '—'}</pre>
        <h3 style={{margin: '14px 0 8px'}}>log（tail 200，6s 自动刷新）</h3>
        <pre className="logbox">{lines == null ? '加载中…' : lines.join('\n') || '(空)'}</pre>
        <h3 style={{margin: '14px 0 8px'}}>meta</h3>
        <pre className="logbox" style={{maxHeight: 150}}>{jsonShort(job.meta, 2000)}</pre>
      </div>
    </>
  );
}

export default function Jobs() {
  const [filters, setFilters] = useState({status: '', backend: ''});
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 50;
  const {data, error, loading, refresh} = usePolling('/jobs', {...filters, limit, offset});
  const {data: events} = usePolling('/events', {limit: 50}, 8000);

  const setF = (k, v) => { setFilters(f => ({...f, [k]: v})); setOffset(0); };
  const cancel = async (id) => {
    await apiPost(`/jobs/${id}/cancel`, {});
    refresh();
  };

  return (
    <div>
      <h2>Jobs</h2>
      <div className="filters">
        <select value={filters.status} onChange={e => setF('status', e.target.value)}>
          <option value="">全部状态</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.backend} onChange={e => setF('backend', e.target.value)}>
          <option value="">全部 backend</option>
          <option>kubernetes</option>
          <option>ssh-gpu</option>
          <option>mock</option>
        </select>
        {loading && <span className="muted">刷新中…</span>}
      </div>

      {error && <p className="muted">API 错误：{error}</p>}
      <div className="panel" style={{padding: 0, overflowX: 'auto'}}>
        <table>
          <thead>
            <tr>
              <th>id</th><th>name</th><th>backend</th><th>stage</th><th>status</th>
              <th>exit</th><th>submitted</th><th>finished</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            {(data || []).map(j => (
              <tr key={j.id} onClick={() => setSelected(j)}>
                <td className="mono">{j.id}</td>
                <td className="mono">{j.name}</td>
                <td>{j.backend}</td>
                <td><Stage s={j.stage} /></td>
                <td><Badge v={j.status} /></td>
                <td>{j.exit_code ?? '—'}</td>
                <td className="muted mono">{fmtTime(j.submitted_at)}</td>
                <td className="muted mono">{fmtTime(j.finished_at)}</td>
                <td onClick={e => e.stopPropagation()}>
                  {['queued', 'dispatched', 'running'].includes(j.status) && (
                    <button className="btn danger" onClick={() => cancel(j.id)}>取消</button>)}
                </td>
              </tr>))}
            {data && data.length === 0 && (
              <tr><td colSpan={9} className="muted">无 job</td></tr>)}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
        <button className="btn" disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}>上一页</button>
        <button className="btn" disabled={!data || data.length < limit}
                onClick={() => setOffset(offset + limit)}>下一页</button>
        <span className="muted">offset {offset}</span>
      </div>
      {selected && <LogDrawer job={selected} onClose={() => setSelected(null)} />}

      <div className="panel" style={{marginTop: 16}}>
        <h3>事件流（原始运维日志）</h3>
        {(events || []).map(e => (
          <div className="eventline" key={e.id}>
            <span className="t">{fmtTime(e.created_at).slice(11)}</span>
            <span className={`badge ${e.level}`}>{e.kind || e.level}</span>
            <span className="muted"> {e.message}</span>
          </div>))}
        {(!events || events.length === 0) && <p className="muted">暂无事件</p>}
      </div>
    </div>
  );
}
